import theme from "@elegantstack/flow-ui-theme/src/theme/index";
import colors from "@elegantstack/flow-ui-theme/src/theme/color-preset";
import beta from "@elegantstack/flow-ui-theme/src/theme/colors";
import omega from "@elegantstack/flow-ui-theme/src/theme/colors";

const CustomTheme = () => {
    return {
        ...theme,
        colors: {
            ...theme.colors,
            alphaLighter: '#afc6c9',
            alphaLight: '#8eafb3',
            alpha: '#5e8d93', //primula color
            alphaDark: '#4b7176',
            alphaDarker: '#385558',
            /* Contrast (WCAG AA, measured with Lighthouse):
               `text` was #718096 = 4.01:1 on white, `omega` was #a0aec0 =
               2.25:1. Both are used for small meta text (dates, read time,
               card footers), so they are darkened to clear 4.5:1 while
               staying in the same blue-grey family. */
            text: '#52606f',
            /* Both muted greys are used for small text on the light surfaces
               the site stacks (#fff, #f8f8f8, #edf2f7, #e2e8f0). The originals
               measured 4.01 and 2.25 on white and worse on the greys; these
               values clear 4.5:1 on the darkest surface in use. */
            omega: '#5a6879',
            omegaDark: '#52606f',
            successLight: colors.green[100],
            success: colors.green[500],
            /* The theme's error colour was #ed8936 and is used as TEXT, where it
               measured 2.4:1 on the page background. Darkened so it clears AA on
               every surface the site uses (white 6.5, #f8f8f8 6.2, #edf2f7 5.8,
               #e2e8f0 5.3). It stays an orange, not a red. */
            errorLight: colors.orange[200],
            error: '#9c4221',
            modes: {
                dark: {
                    // Reverse alpha
                    alphaLighter: '#385558',
                    alphaLight: '#4b7176',
                    alphaDark: '#8eafb3',
                    alphaDarker: '#afc6c9',

                    // Reverse beta
                    betaLighter: beta.betaDarker,
                    betaLight: beta.betaDark,
                    betaDark: beta.betaLight,
                    betaDarker: beta.betaLighter,

                    // Reverse omega
                    omegaLighter: omega.omegaDarker,
                    omegaLight: omega.omegaDark,
                    omegaDark: omega.omegaLight,
                    omegaDarker: omega.omegaLighter,

                    text: colors.gray[400],
                    article: colors.gray[300],
                    heading: colors.gray[200],

                    background: colors.gray[900],
                    contentBg: colors.gray[800],
                    headerBg: colors.gray[800],
                    footerBg: colors.gray[800],

                    mute: colors.gray[600]
                }
            }
        },
        /* Card padding.
           
           The theme ships `p: [4, 0]` on the horizontal-hero body: 2rem on
           mobile, then ZERO from 640px up. That was written when the hero card
           had no surface of its own (transparent background, sitting directly on
           the page), so flush text looked fine. The carousel now gives each slide
           a real surface - radius, border, shadow - which makes zero padding read
           as text glued to the card edge, especially on a phone in landscape
           where the card is wide and the text column sits right at the boundary.
           Restore padding at every breakpoint. */
        cards: {
            ...(theme.cards || {}),
            'horizontal-hero': {
                ...((theme.cards && theme.cards['horizontal-hero']) || {}),
                body: {
                    ...((theme.cards && theme.cards['horizontal-hero'] && theme.cards['horizontal-hero'].body) || {}),
                    p: [4, 4],
                    /* Padding costs vertical space too: on a 375 px-high
                       landscape phone the extra 64 px pushed the carousel
                       markers back below the fold. 1rem keeps a real buffer
                       while still fitting the card, the heading and the markers
                       on one screen. */
                    '@media (max-height: 560px) and (orientation: landscape)': {
                        p: 3
                    }
                }
            }
        },
        /* Short landscape viewports (phones on their side): the hero card
           uses the h1 scale and, at ~430 px of height, the card grew taller
           than the viewport - which pushed the carousel markers below the
           fold, leaving no visible affordance at all. Capping the type
           scale keeps the heading, the excerpt and the markers inside one
           screen. */
        text: {
            ...(theme.text || {}),
            h1: {
                ...((theme.text && theme.text.h1) || {}),
                '@media (max-height: 560px) and (orientation: landscape)': {
                    fontSize: 5
                }
            }
        },
        styles: {
            ...theme.styles,
            /* WCAG 1.4.1 (Use of Color): links inside running text must be
               distinguishable without colour. Measured on an article body:
               link #5e8d93 against surrounding text #4a5568 is 2.04:1, below
               the 3:1 Lighthouse requires. Underlining prose links satisfies
               both 1.4.1 and the audit, and leaves nav/buttons untouched
               because they carry their own link variants. */
            p: {
                ...(theme.styles && theme.styles.p),
                a: {
                    textDecoration: `underline`,
                    textUnderlineOffset: `0.15em`
                }
            }
        },
        links: {
            ...theme.links,
            /* Link text uses the brand teal, which measured 3.68:1 on white and
               2.98:1 on the grey surfaces the site also uses - below AA for
               body-size text. Rendered separately from the `alpha` palette key
               so buttons and badges keep their brand background. */
            normal: {
                color: `#3f666b`,
                textDecoration: `none`,
                userSelect: `none`,
                ':visited': { color: `#3f666b` },
                ':hover': { color: `alphaDarker` }
            }
        }
    }
}

export default CustomTheme;